import type { OutreachFollowup, OutreachRow } from '@discovr/types';
import { shortDate } from './shortDate';

/** Agent reasons (contract §6: wants_call…other) plus Go's rule reasons (over_ceiling, currency, quote_after_agreed — Ruling 15; currency_mismatch is the older key for currency), the reply-on-closed-row reasons (rejected_reply, removed_reply) and the 2026-10-21 rulings (unsubscribed or bounced after the hand-off, brand_counter, reminder_due) and R15-10's reply_waiting. */
export const ESCALATION_REASON_LABELS: Record<string, string> = {
  wants_call: 'Wants a call',
  legal: 'Legal',
  complaint: 'Complaint',
  contract: 'Contract question',
  off_scope: 'Off scope',
  tone: 'Tone',
  other: 'Other',
  over_ceiling: 'Over the ceiling',
  currency_mismatch: 'Foreign currency quote',
  currency: 'Foreign currency quote',
  unsubscribed: 'Unsubscribed after hand-off',
  bounced: 'Bounced after hand-off',
  brand_counter: 'Brand asked for less',
  reminder_due: 'Reminder due',
  reply_waiting: 'Waiting 2 days',
  quote_after_agreed: 'New quote after agreement',
  parse_failed: "Couldn't read reply",
  rejected_reply: 'Rejected creator replied',
  removed_reply: 'Removed creator replied',
  followup_due_while_paused: 'Follow-up was due while paused',
  agent_no_answer: "Assistant didn't answer",
};

/** R17-60: a job whose callback never came back carries the note `<kind>:deadline_exceeded`; null for any other note. */
export function lostJobKind(row: Pick<OutreachRow, 'escalationReason' | 'escalationNote'>): string | null {
  if (row.escalationReason !== 'agent_no_answer') return null;
  const m = /^([a-z_]+):deadline_exceeded$/.exec(row.escalationNote ?? '');
  return m ? m[1]! : null;
}

export const AWAITING_REPLY_LABEL = 'Needs you · New reply';
export const AWAITING_REPLY_HINT = 'The creator replied and nobody has answered yet';

export function escalationLabel(reason?: string): string {
  if (!reason) return 'Escalated';
  return ESCALATION_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');
}

export function escalationTitle(row: Pick<OutreachRow, 'escalationSource' | 'escalationNote'>): string {
  const who = row.escalationSource === 'agent' ? 'Flagged by the assistant' : 'Flagged by a rule';
  return row.escalationNote ? `${who} — ${row.escalationNote}` : who;
}

/** Ruling 1's row sub-label for a hold with no date; a dated wait reads as the follow-up countdown instead. */
export function followupDeferredLabel(row: Pick<OutreachRow, 'followupDeferred'>): string | null {
  const d = row.followupDeferred;
  if (!d || d.action === 'none' || d.scheduledAt) return null;
  return 'Creator asked to wait';
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** countdown: plain text; overdue: the red pill; due: the Follow up pill; failed: the red pill for a failed automatic step. */
export type FollowupLabel = { tone: 'countdown' | 'overdue' | 'due' | 'failed'; text: string };

/** The follow-up is due once Go says so, or once its time passes while the row is on screen. */
export function isFollowupDue(followup: OutreachFollowup | null | undefined, now: Date = new Date()): boolean {
  if (!followup) return false;
  return followup.due || Date.parse(followup.dueAt) <= now.getTime();
}

/** Owner 2026-10-13: a day is a full 24 hours between real instants, never a calendar day, in any zone. */
const wholeDays = (ms: number) => Math.floor(ms / DAY_MS);
const dayCount = (days: number) => `${days} ${days === 1 ? 'day' : 'days'}`;

/** R17-62: a date that has passed reads "Overdue by N days" (whole days; 1 = "Overdue by 1 day"), the same day "Due today". */
export const overdueText = (pastDays: number) => (pastDays < 1 ? 'Due today' : `Overdue by ${dayCount(pastDays)}`);

/** R17-62: the date `iso` as the follow-up line wants it once it has passed ("Overdue by 3 days"); null while it is still ahead. */
export function overdueLabel(iso: string, now: Date): string | null {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms) || ms > now.getTime()) return null;
  return overdueText(wholeDays(now.getTime() - ms));
}

/** F4: an automatic step counts down to nextAt in 24-hour units and stays a quiet countdown once it is past, since Go
 *  sends it without us. A paused row shows none (its pill speaks), nor does a retrying or queued step. A failed step
 *  is shown whatever the switch says, since the chain is stalled; the countdown needs the campaign's switch ON. */
function stepsRemainingLabel(row: Pick<OutreachRow, 'followupsPausedAt'>, f: OutreachFollowup, now: Date, autoFollowups: boolean, forRow: boolean): FollowupLabel | null {
  if (row.followupsPausedAt) return null;
  if (f.state === 'failed') return { tone: 'failed', text: 'Follow-up failed' };
  if (!autoFollowups || (f.state && f.state !== 'scheduled')) return null;
  const left = Date.parse(f.nextAt ?? f.dueAt) - now.getTime();
  if (!Number.isFinite(left)) return null;
  if (left < 0) return { tone: 'countdown', text: overdueText(wholeDays(-left)) };
  return countdownLabel(left, forRow);
}

/** The composer pill reads "Follow up in N days"; the row's line reads "Follow-up in N days". */
function countdownLabel(left: number, forRow: boolean): FollowupLabel {
  const lead = forRow ? 'Follow-up' : 'Follow up';
  if (left < DAY_MS) return { tone: 'countdown', text: `${lead} today` };
  const days = Math.ceil(left / DAY_MS);
  return { tone: 'countdown', text: `${lead} in ${days} ${days === 1 ? 'day' : 'days'}` };
}

/** Owner 2026-10-12: a creator's wait counts down ("Follow up in N days", a part day rounds up, under 24 hours reads
 *  "Follow up today"), then says how long it is overdue on red (owner 2026-10-15, whole 24-hour days since the due
 *  instant); an unanswered email shows the Follow up pill, and red plain English once a whole day overdue. Zones play no part in the counts.
 *  `autoFollowups` is the campaign's Automatic follow-ups switch; with it OFF no automatic-step countdown shows.
 *  `forRow` words due, overdue and countdown as the Outreach row's line ("Follow-up in 3 days") instead of the composer's pill. */
export function followupLabel(row: Pick<OutreachRow, 'followup' | 'followupsPausedAt'>, now: Date = new Date(), autoFollowups = true, forRow = false): FollowupLabel | null {
  const f = row.followup;
  if (!f) return null;
  if (f.kind === 'steps_remaining') return stepsRemainingLabel(row, f, now, autoFollowups, forRow);
  const dueMs = Date.parse(f.dueAt);
  const pastDays = Number.isFinite(dueMs) ? wholeDays(now.getTime() - dueMs) : f.overdueDays;
  if (f.kind === 'no_reply') {
    if (forRow) return { tone: pastDays < 1 ? 'due' : 'overdue', text: overdueText(pastDays) };
    return pastDays < 1 ? { tone: 'due', text: 'Follow up' } : { tone: 'overdue', text: `${overdueText(pastDays)} — no reply yet. Follow up now.` };
  }
  if (isFollowupDue(f, now)) {
    if (forRow) return { tone: 'overdue', text: overdueText(pastDays) };
    return { tone: 'overdue', text: pastDays < 1 ? 'Follow up today' : `${overdueText(pastDays)} — follow up now.` };
  }
  const left = dueMs - now.getTime();
  if (!Number.isFinite(left)) return null;
  return countdownLabel(left, forRow);
}

/** The profile drawer's Next follow-up (owner 2026-10-15): a creator's asked-for date and the no-reply date always show;
 *  the automatic step's date only with the campaign's switch ON. Null when no date exists. */
export function nextFollowupDate(row: Pick<OutreachRow, 'followup' | 'nextFollowupAt'>, autoFollowups: boolean): string | null {
  const f = row.followup;
  if (f && f.kind !== 'steps_remaining') return Number.isFinite(Date.parse(f.dueAt)) ? f.dueAt : null;
  return autoFollowups && row.nextFollowupAt ? row.nextFollowupAt : null;
}

export const SEQUENCE_ENDED_LABEL = 'Sequence ended';

/** E9-86: Go sets next_followup_at from the earliest open step and sends no followup object once none is left (a reopen
 *  to contacted arms nothing). A cancelled last follow-up (Stop on the last step) counts as ended. A paused row keeps its own label; a failed step still shows as a followup, so the sequence is not over. */
export function isSequenceEnded(
  row: Pick<OutreachRow, 'status' | 'outreachState' | 'followup' | 'nextFollowupAt' | 'followupsPausedAt' | 'followupDeferred' | 'lastMessage'>,
): boolean {
  return (
    row.status === 'outreach' &&
    row.outreachState === 'contacted' &&
    (row.lastMessage?.status === 'sent' || (row.lastMessage?.kind === 'followup' && row.lastMessage.status === 'cancelled')) &&
    !row.followup &&
    !row.nextFollowupAt &&
    !row.followupsPausedAt &&
    (!row.followupDeferred || row.followupDeferred.action === 'none')
  );
}

/** Go Task 13: "Escalation cleared by <email> · 27 Sep — <note>". Only a human clears
 *  (scope 3), so the copy names the person, never "the assistant". Null while a live
 *  escalation stands (Go blanks the fields on re-flag; the escalatedAt guard covers a stale read). */
export function escalationClearedLabel(
  row: Pick<OutreachRow, 'escalatedAt' | 'escalationClearedBy' | 'escalationClearedAt' | 'escalationClearedNote'>,
  zone?: string,
): string | null {
  if (row.escalatedAt || !row.escalationClearedAt) return null;
  const by = row.escalationClearedBy ? ` by ${row.escalationClearedBy}` : '';
  const note = row.escalationClearedNote ? ` — ${row.escalationClearedNote}` : '';
  return `Escalation cleared${by} · ${shortDate(new Date(row.escalationClearedAt), zone)}${note}`;
}
